import { useState } from 'react';
import {
  ArrowRightCircle,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  FileSearch,
  ClipboardCheck,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useReviewSources,
  useReviewStats,
  useAdvanceToFullText,
  useDetectConflicts,
  useUpdateReview,
} from '@/features/review/hooks/useReview';
import { useTeam } from '@/features/team/hooks/useTeam';
import { useAuthStore } from '@/store/authStore';
import type { Review } from '@/features/review/types';

interface PhaseManagerProps {
  review: Review;
  className?: string;
}

const PHASE_CONFIG = {
  screening: {
    label: 'Screening',
    icon: FileSearch,
    color: 'bg-blue-100 text-blue-700',
    next: 'full_text' as const,
    nextLabel: 'Advance to Full Text',
  },
  full_text: {
    label: 'Full Text',
    icon: ClipboardCheck,
    color: 'bg-emerald-100 text-emerald-700',
    next: 'completed' as const,
    nextLabel: 'Complete Review',
  },
  extraction: {
    label: 'Extraction',
    icon: ClipboardCheck,
    color: 'bg-purple-100 text-purple-700',
    next: 'completed' as const,
    nextLabel: 'Complete Review',
  },
  completed: {
    label: 'Completed',
    icon: CheckCircle2,
    color: 'bg-slate-100 text-slate-700',
    next: null,
    nextLabel: '',
  },
};

export function PhaseManager({ review, className }: PhaseManagerProps) {
  const [showConfirm, setShowConfirm] = useState(false);
  const user = useAuthStore((s) => s.user);
  const activePhase = review.status === 'full_text' ? 'full_text' : 'screening';

  const { data: stats } = useReviewStats(review.id, activePhase);
  const { data: sources } = useReviewSources(review.id, activePhase);
  const { data: team } = useTeam(review.team_id);
  const advanceToFullText = useAdvanceToFullText();
  const detectConflicts = useDetectConflicts();
  const updateReview = useUpdateReview();

  const phaseConfig = PHASE_CONFIG[review.status] ?? PHASE_CONFIG.screening;
  const PhaseIcon = phaseConfig.icon;

  const totalSources = sources?.length ?? 0;
  const screened = stats?.screened ?? 0;
  const progressPct = totalSources > 0 ? (screened / totalSources) * 100 : 0;
  const isAdvancing = advanceToFullText.isPending || updateReview.isPending;

  // Only owners and admins can advance phases
  const isAdmin = team?.members.some(
    (m) =>
      m.user_id === user?.id &&
      ['owner', 'admin'].includes(m.role) &&
      m.accepted_at !== null
  ) ?? false;

  const handleAdvance = async () => {
    setShowConfirm(false);

    if (review.status === 'screening') {
      // Detect conflicts first
      await detectConflicts.mutateAsync({
        reviewId: review.id,
        phase: 'screening',
      });
      // Then advance
      await advanceToFullText.mutateAsync(review.id);
    } else if (review.status === 'full_text') {
      // Detect conflicts
      await detectConflicts.mutateAsync({
        reviewId: review.id,
        phase: 'full_text',
      });
      // Complete the review
      await updateReview.mutateAsync({
        reviewId: review.id,
        status: 'completed',
      });
    }
  };

  return (
    <>
      <Card className={className}>
        <CardContent className="p-4 space-y-3">
          {/* Phase badge + progress */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Badge className={cn('text-xs gap-1', phaseConfig.color)}>
                <PhaseIcon className="h-3.5 w-3.5" />
                {phaseConfig.label}
              </Badge>
              {stats && (
                <span className="text-xs text-slate-500">
                  {stats.screened} / {totalSources} reviewed
                </span>
              )}
            </div>
            {stats && stats.conflicts > 0 && (
              <Badge variant="outline" className="text-xs text-amber-600 border-amber-200 gap-1">
                <AlertTriangle className="h-3 w-3" />
                {stats.conflicts - stats.resolved_conflicts} unresolved
              </Badge>
            )}
          </div>

          {/* Progress bar */}
          <Progress value={progressPct} className="h-2" />

          {/* Stats row */}
          {stats && (
            <div className="flex items-center gap-4 text-xs text-slate-500">
              <span className="text-emerald-600">{stats.included} included</span>
              <span className="text-rose-600">{stats.excluded} excluded</span>
              <span className="text-amber-600">{stats.maybe} maybe</span>
            </div>
          )}

          {/* Advance button — only visible to owners/admins */}
          {phaseConfig.next && review.status !== 'completed' && isAdmin && (
            <Button
              className="w-full"
              onClick={() => setShowConfirm(true)}
              disabled={isAdvancing || totalSources === 0}
            >
              {isAdvancing ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <ArrowRightCircle className="h-4 w-4 mr-1.5" />
              )}
              {phaseConfig.nextLabel}
            </Button>
          )}

          {review.status === 'completed' && (
            <div className="text-center py-2">
              <CheckCircle2 className="h-6 w-6 text-emerald-500 mx-auto mb-1" />
              <p className="text-xs text-slate-500">Review completed</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirmation dialog */}
      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{phaseConfig.nextLabel}?</AlertDialogTitle>
            <AlertDialogDescription>
              {review.status === 'screening'
                ? 'This will detect conflicts, then advance all included and maybe sources to full-text review. Excluded sources will not advance.'
                : 'This will detect any remaining conflicts and mark the review as completed. Make sure all sources have been reviewed.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleAdvance}>
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
